import React from 'react';
import type { PhotoAnalysis } from '../types';

const QUALITY_THEME = {
  professional: { label: 'プロ品質', color: 'text-leaf-700' },
  amateur:      { label: 'アマチュア', color: 'text-amber-700' },
  poor:         { label: '要緊急対応', color: 'text-rose-700' },
} as const;

const PhotoQuality: React.FC<{ analysis: PhotoAnalysis }> = ({ analysis }) => {
  const theme = QUALITY_THEME[analysis.overall_quality] || QUALITY_THEME.amateur;

  return (
    <section className="py-4 border-b border-slate-200">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <h3 className="text-[13px] font-semibold text-slate-900">写真クオリティ診断</h3>
        <div className="text-right">
          <span className={`text-[12px] font-semibold ${theme.color}`}>{theme.label}</span>
          <span className="text-[11px] text-slate-500 ml-2">スコア {analysis.overall_score}/100</span>
          {analysis.analyzed_photo_count !== undefined && (
            <span className="text-[11px] text-slate-400 ml-1">（{analysis.analyzed_photo_count}枚分析）</span>
          )}
        </div>
      </div>

      <div className="border-l-2 border-slate-200 pl-3 mb-4">
        <div className="text-[10px] font-semibold text-slate-500 mb-1">第一印象（0.8秒）</div>
        <p className="text-[12px] text-slate-700 leading-relaxed">{analysis.first_impression}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
        {analysis.strengths.length > 0 && (
          <div>
            <h4 className="text-[11px] font-semibold text-leaf-700 mb-2">良い点</h4>
            <ul className="space-y-1 text-[12px] text-slate-700 list-disc pl-4">
              {analysis.strengths.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        )}
        {analysis.weaknesses.length > 0 && (
          <div>
            <h4 className="text-[11px] font-semibold text-rose-700 mb-2">改善が必要な点</h4>
            <ul className="space-y-1 text-[12px] text-slate-700 list-disc pl-4">
              {analysis.weaknesses.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
      </div>

      {analysis.missing_subject_types && analysis.missing_subject_types.length > 0 && (
        <div className="mb-4 pb-4 border-b border-slate-100">
          <h4 className="text-[11px] font-semibold text-slate-700 mb-1">不足している被写体タイプ</h4>
          <div className="text-[12px] text-slate-700">{analysis.missing_subject_types.join(' / ')}</div>
        </div>
      )}

      <div className="mb-4">
        <h4 className="text-[11px] font-semibold text-slate-700 mb-2">改善提案</h4>
        <ol className="space-y-1 text-[12px] text-slate-700 list-decimal pl-5 marker:text-leaf-600 marker:font-semibold">
          {analysis.recommendations.map((r, i) => <li key={i} className="pl-1">{r}</li>)}
        </ol>
      </div>

      {analysis.competitor_gap && (
        <div>
          <h4 className="text-[11px] font-semibold text-slate-700 mb-1">競合との決定的な差</h4>
          <p className="text-[12px] text-slate-600 leading-relaxed">{analysis.competitor_gap}</p>
        </div>
      )}
    </section>
  );
};

export default PhotoQuality;
